import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';
import './CookingNotes.css';

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function CookingNotes({ recipeId }) {
  const { language } = useLanguage();
  const t = translations[language];

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId]);

  async function fetchNotes() {
    setLoading(true);
    const { data, error } = await supabase
      .from('recipe_notes')
      .select('*')
      .eq('recipe_id', recipeId)
      .order('created_at', { ascending: false });
    if (!error) setNotes(data || []);
    setLoading(false);
  }

  async function handleSave() {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSaving(true);
    const { error } = await supabase
      .from('recipe_notes')
      .insert({ recipe_id: recipeId, note: trimmed });
    if (!error) {
      setText('');
      await fetchNotes();
    }
    setSaving(false);
  }

  async function handleEditSave(id) {
    const trimmed = editText.trim();
    if (!trimmed) return;
    const { error } = await supabase
      .from('recipe_notes')
      .update({ note: trimmed })
      .eq('id', id);
    if (!error) {
      setEditingId(null);
      setEditText('');
      await fetchNotes();
    }
  }

  async function handleDelete(id) {
    setDeletingId(id);
    const { error } = await supabase
      .from('recipe_notes')
      .delete()
      .eq('id', id);
    if (!error) {
      setNotes((prev) => prev.filter((n) => n.id !== id));
    }
    setDeletingId(null);
  }

  function startEdit(note) {
    setEditingId(note.id);
    setEditText(note.note);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditText('');
  }

  return (
    <section className="cooking-notes">
      <h2>{t.cookingNotesTitle}</h2>

      <div className="cooking-notes__add">
        <textarea
          className="cooking-notes__input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t.cookingNotesPlaceholder}
          rows={3}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSave();
          }}
        />
        <button
          className="cooking-notes__save-btn"
          onClick={handleSave}
          disabled={saving || !text.trim()}
        >
          {saving ? t.cookingNotesSaving : t.cookingNotesSave}
        </button>
      </div>

      {loading ? (
        <p className="cooking-notes__empty">{t.loading}</p>
      ) : notes.length === 0 ? (
        <p className="cooking-notes__empty">{t.cookingNotesEmpty}</p>
      ) : (
        <ul className="cooking-notes__list">
          {notes.map((note) => (
            <li key={note.id} className="cooking-notes__item">
              {editingId === note.id ? (
                <div className="cooking-notes__edit">
                  <textarea
                    className="cooking-notes__input"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={3}
                    autoFocus
                  />
                  <div className="cooking-notes__edit-actions">
                    <button
                      className="cooking-notes__save-btn"
                      onClick={() => handleEditSave(note.id)}
                      disabled={!editText.trim()}
                    >
                      {t.cookingNotesSave}
                    </button>
                    <button className="cooking-notes__cancel-btn" onClick={cancelEdit}>
                      {t.cancel}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="cooking-notes__text">{note.note}</p>
                  <div className="cooking-notes__meta">
                    <span className="cooking-notes__date">{formatDate(note.created_at)}</span>
                    <button
                      className="cooking-notes__action-btn"
                      onClick={() => startEdit(note)}
                    >
                      {t.cookingNotesEdit}
                    </button>
                    <button
                      className="cooking-notes__action-btn cooking-notes__action-btn--delete"
                      onClick={() => handleDelete(note.id)}
                      disabled={deletingId === note.id}
                    >
                      {deletingId === note.id ? '…' : t.cookingNotesDelete}
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
